import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { hasPermission, ResourceType } from '@/types/permission.types';
import { PlanType, SubscriptionStatus } from '@/types/tenant.types';

export interface User {
  id: string;
  tenantId: string;
  email: string;
  name: string;
  role: string; // Display name from backend (e.g. "Administrador", "Gerente")
  avatarUrl?: string;
  // From JWT claims
  permissions: string[]; // Array of "resource:action" strings
  subscriptionStatus: SubscriptionStatus;
  plan: PlanType;
  isSuperAdmin?: boolean; // Platform-level super admin (from is_super_admin JWT claim)
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  idToken: string;
  expiresIn: number;
}

interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  // Computed permission set for fast lookups
  permissionSet: Set<string>;
  // Actions
  setAuth: (user: User, tokens: AuthTokens) => void;
  setTokens: (tokens: AuthTokens) => void;
  setUser: (user: User, role: { id: string; name: string }, permissions: string[]) => void;
  updateTokens: (tokens: Partial<AuthTokens>) => void;
  updateUser: (user: Partial<User>) => void;
  logout: () => void;
  // Permission helpers
  hasPermission: (resource: ResourceType, action: string) => boolean;
  isAdmin: () => boolean;
  canAccess: (resource: ResourceType) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      isAuthenticated: false,
      permissionSet: new Set<string>(),

      setAuth: (user, tokens) =>
        set({
          user,
          tokens,
          isAuthenticated: true,
          permissionSet: new Set(user.permissions),
        }),

      setTokens: (tokens) =>
        set({
          tokens,
        }),

      setUser: (userData, role, permissions) =>
        set(() => {
          const user: User = {
            id: userData.id,
            tenantId: userData.tenantId,
            email: userData.email,
            name: userData.name,
            role: role.name,
            avatarUrl: userData.avatarUrl,
            permissions,
            subscriptionStatus: userData.subscriptionStatus || 'active',
            plan: userData.plan || 'free',
            isSuperAdmin: userData.isSuperAdmin,
          };
          return {
            user,
            isAuthenticated: true,
            permissionSet: new Set(permissions),
          };
        }),

      updateTokens: (newTokens) =>
        set((state) => ({
          tokens: state.tokens ? { ...state.tokens, ...newTokens } : null,
        })),

      updateUser: (updates) =>
        set((state) => {
          if (!state.user) return state;
          const updatedUser = { ...state.user, ...updates };
          return {
            user: updatedUser,
            permissionSet: new Set(updatedUser.permissions),
          };
        }),

      logout: () =>
        set({
          user: null,
          tokens: null,
          isAuthenticated: false,
          permissionSet: new Set<string>(),
        }),

      // Permission helpers
      hasPermission: (resource: ResourceType, action: string) => {
        const { permissionSet } = get();
        return hasPermission(permissionSet, resource, action);
      },

      isAdmin: () => {
        const { permissionSet, user } = get();
        return permissionSet.has('*:*') || user?.isSuperAdmin === true;
      },

      canAccess: (resource: ResourceType) => {
        const { permissionSet } = get();
        // Check if user has any permission on the resource
        return hasPermission(permissionSet, resource, 'view');
      },
    }),
    {
      name: 'campanha-flow-auth',
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
      }),
      // Rehydrate permission set on load
      onRehydrateStorage: () => (state) => {
        if (state?.user?.permissions) {
          state.permissionSet = new Set(state.user.permissions);
        }
      },
    }
  )
);
